import { useAsyncData } from '#imports'

import type { ProductsBlockApiProps } from '@backmarket/http-api/src/api-specs-content/models/products-block'
import type { RefinedProductsApiProps } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import algoliasearch from 'algoliasearch'

import { mapAlgoliaResultsToProducts } from './algolia/algoliaAdapter'
import type { AlgoliaProductHit } from './algolia/types'
import {
  type InitializableAlgoliaClient,
  useAlgoliaWithDeps,
} from './algolia/useAlgolia'

type Params =
  | Pick<ProductsBlockApiProps, 'productQueries'>
  | Pick<RefinedProductsApiProps, 'productQuery'>

function buildAlgoliaParams(props: Params) {
  const productQueries =
    'productQueries' in props ? props.productQueries : [props.productQuery]

  const { apiKey } = productQueries[0]

  const queries = productQueries.map(({ indexName, params }) => ({
    indexName,
    params,
  }))

  return { apiKey, queries }
}

export const useSearchProductsWithDeps =
  (initializableAlgoliaClient: InitializableAlgoliaClient<AlgoliaProductHit>) =>
  (
    props: Params,
    {
      server = true,
      immediate = true,
    }: { server?: boolean; immediate?: boolean } = {},
  ) => {
    const filter =
      'productQuery' in props ? props.productQuery.params.filters : ''
    const uniqueKey = getAsyncDataKey(`cms-products-${filter}`, props)
    const { search } = useAlgoliaWithDeps({
      initializableAlgoliaClient,
      algoliaSearchOptionType: 'search',
    })

    return useAsyncData(
      uniqueKey,
      async () => {
        const algoliaParams = buildAlgoliaParams(props)

        const algoliaResponse = await search(algoliaParams)

        return mapAlgoliaResultsToProducts(algoliaResponse)
      },
      {
        getCachedData(key, nuxtApp) {
          return nuxtApp.payload.data[key]
        },
        // this allows to avoid duplicate requests when sent at the same time
        // this is useful for EnhancedCarouselBulkFetch which also calls useSearchProducts
        dedupe: 'defer',
        server,
        immediate,
      },
    )
  }

export function useSearchProducts(
  params: Params,
  opts: { server?: boolean; immediate?: boolean },
) {
  return useSearchProductsWithDeps(
    algoliasearch as InitializableAlgoliaClient<AlgoliaProductHit>,
  )(params, opts)
}
